import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { reportErrorToNewRelic } from '@/lib/newrelic/reportError';

const HYDRATION_PATTERNS = [
  /hydration failed/i,
  /error while hydrating/i,
  /server-rendered html/i,
  /text content does not match server-rendered html/i,
];

const MINIFIED_INVARIANT_PATTERN = /minified react error #\d+/i;

const TRACKED_REACT_ERROR_PATTERNS = [MINIFIED_INVARIANT_PATTERN, ...HYDRATION_PATTERNS];

const MAX_REPORTS_PER_SESSION = 3;

function isTrackedReactErrorMessage(message: string): boolean {
  return TRACKED_REACT_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

function getReactInvariantCode(message: string): string | undefined {
  const match = message.match(/minified react error #(\d+)/i);
  return match?.[1];
}

function getReactErrorCategory(
  message: string
): 'react_hydration' | 'react_minified_invariant' | 'react_error' {
  if (HYDRATION_PATTERNS.some((pattern) => pattern.test(message))) {
    return 'react_hydration';
  }
  if (MINIFIED_INVARIANT_PATTERN.test(message)) {
    return 'react_minified_invariant';
  }
  return 'react_error';
}

function getErrorMessage(value: unknown): string {
  if (value instanceof Error) return value.message;
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'message' in value) {
    const { message } = value as { message?: unknown };
    return typeof message === 'string' ? message : '';
  }
  return '';
}

export default function useReactErrorTelemetry() {
  const router = useRouter();

  // Guardrails to prevent event storms: - report each unique message once per route
  // - hard-cap total reports emitted by this hook per session
  const reportCountRef = useRef(0);
  const dedupeRef = useRef<Set<string>>(new Set());
  const isReportingRef = useRef(false);
  const routePathRef = useRef(router.pathname);

  useEffect(() => {
    routePathRef.current = router.pathname;
  }, [router.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (process.env.NEXT_PUBLIC_NEW_RELIC_DISABLED === 'true') return undefined;

    // Central reporter: filters, de-duplicates, categorizes, and forwards to New Relic.
    const reportReactError = (
      source: 'window.error' | 'window.unhandledrejection',
      error: Error
    ) => {
      // Never let telemetry throw or recursively trigger itself.
      if (isReportingRef.current) return;

      try {
        const message = error.message || '';
        if (!isTrackedReactErrorMessage(message)) return;
        if (reportCountRef.current >= MAX_REPORTS_PER_SESSION) return;

        const fingerprint = `${routePathRef.current}:${message}`;
        if (dedupeRef.current.has(fingerprint)) return;
        dedupeRef.current.add(fingerprint);
        reportCountRef.current += 1;

        isReportingRef.current = true;

        // Include attributes that help correlate spikes with route/build and bucket errors by invariant code/category in New Relic queries.
        reportErrorToNewRelic(error, {
          errorSource: source,
          errorCategory: getReactErrorCategory(message),
          reactInvariantCode: getReactInvariantCode(message) || 'unknown',
          routePath: routePathRef.current,
          buildId: window.__NEXT_DATA__?.buildId || 'unknown',
        });
      } catch {
        // Swallow telemetry errors: monitoring should never impact app runtime behavior.
      } finally {
        isReportingRef.current = false;
      }
    };

    // Browser runtime errors (e.g. sync throw during render/hydration).
    const onError = (event: ErrorEvent) => {
      const fromEvent = event.error instanceof Error ? event.error : undefined;
      const message = getErrorMessage(fromEvent ?? event.message);
      if (!message) return;
      const error = fromEvent ?? new Error(message);
      reportReactError('window.error', error);
    };

    // Promise-based runtime errors not handled by app code.
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const { reason } = event;
      const message = getErrorMessage(reason);
      if (!message) return;
      const error = reason instanceof Error ? reason : new Error(message);
      reportReactError('window.unhandledrejection', error);
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);
}
